// src/components/FloatingContactButton.js
import Lottie from 'lottie-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import phoneRingAnimation from '../assets/phoneRing.json';
import { useConfig } from '../context/ConfigContext';

const FloatingContactButton = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const { config } = useConfig();

    // Default color if config is not loaded yet
    const primaryColor = config?.colors?.primary || '#3B82F6';

    const handleBookingClick = () => {
        navigate('/book/driving-lesson');
        setIsOpen(false);
    };

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Options Menu */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 bg-white rounded-lg shadow-2xl z-50 w-72 overflow-hidden animate-slideUp">
                    <div className="p-4 border-b" style={{ backgroundColor: primaryColor }}>
                        <h3 className="text-white font-semibold text-lg">Get in Touch</h3>
                    </div>

                    <div className="divide-y">
                        {/* Phone Numbers Section - Combined */}
                        <div className="p-4 hover:bg-gray-50 transition-colors animate-slideInRight" style={{ animationDelay: '0.1s' }}>
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: primaryColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-gray-600 mb-2">Call Us</p>
                                    <a
                                        href="tel:7787585859"
                                        className="block font-semibold text-gray-900 hover:underline mb-1"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        (778) 758-5859
                                    </a>
                                    <a
                                        href="tel:8668666630"
                                        className="block font-semibold text-gray-900 hover:underline"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        (866) 866-6630
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* WhatsApp Option */}
                        <a
                            href="https://wa.me/17787585859"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors animate-slideInRight"
                            style={{ animationDelay: '0.2s' }}
                            onClick={() => setIsOpen(false)}
                        >
                            <div className="flex-shrink-0">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="#25D366"
                                    className="h-6 w-6"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-600">WhatsApp</p>
                                <p className="font-semibold text-gray-900">Chat or Call</p>
                            </div>
                        </a>

                        {/* Book Appointment Option - Highlighted */}
                        <button
                            onClick={handleBookingClick}
                            className="flex items-center gap-3 p-4 w-full text-left transition-all hover:opacity-90 animate-slideInRight"
                            style={{ animationDelay: '0.3s' }}
                        >
                            <div className="flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#3B82F6]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-600">Schedule</p>
                                <p className="font-semibold text-[#3B82F6]">Book Your Class</p>
                            </div>
                            <div className="flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Floating Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-6 right-6 p-4 text-white rounded-full shadow-lg hover:scale-110 transition-all focus:outline-none z-50"
                aria-label="Contact options"
                style={{ backgroundColor: primaryColor }}
            >
                {isOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <div className="w-10 h-10">
                        <Lottie
                            animationData={phoneRingAnimation}
                            loop={true}
                        />
                    </div>
                )}
            </button>

            <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
        </>
    );
};

export default FloatingContactButton;

